import { Badge, Box, Divider, Group, Progress, ScrollArea, Table, Text } from '@mantine/core'
import { useEffect, useMemo } from 'react'

import { useLeadExportFailedRows, useLeadExportStatus } from '../hooks'

type ExportError = {
  companyName?: string
  firstName?: string
  lastName?: string
  jobTitle?: string
  email?: string
  error?: string
  timestamp?: string
}

export const LeadExportProgress = ({
  campaignId,
  exportId,
}: {
  exportId: string
  campaignId: number
}) => {
  const {
    data: raw,
    isLoading,
    isError,
  } = useLeadExportStatus(campaignId, exportId, {
    pollIntervalMs: 5000,
    enabled: !!campaignId && !!exportId,
  })

  const isEnabled = Boolean(raw && raw.processed === raw.totalLeads && raw.totalLeads > 0)
  const {
    data: failedData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
  } = useLeadExportFailedRows(campaignId, exportId, isEnabled)

  useEffect(() => {
    if (isEnabled && hasNextPage && !isFetchingNextPage && !isFetching) {
      fetchNextPage()
    }
  }, [isEnabled, hasNextPage, isFetchingNextPage, isFetching, fetchNextPage])
  const failedRows = failedData?.pages.flatMap((p) => p.items) ?? []

  const total = raw?.totalLeads ?? 0
  const processedFallback = (raw?.successCount ?? 0) + (raw?.failedCount ?? 0)
  const processed = typeof raw?.processed === 'number' ? raw.processed : processedFallback
  const current = processed

  const progressValue = useMemo(() => {
    // Prefer server percentage only if it's a meaningful positive number
    if (typeof raw?.percentage === 'number' && raw.percentage > 0) {
      return Math.max(0, Math.min(100, Number(raw.percentage)))
    }

    // If total is missing or zero, fall back to 0
    if (!total || total <= 0) return 0

    // Compute from current/total and keep fractional precision so the UI moves smoothly
    const pct = (current / total) * 100
    const bounded = Math.max(0, Math.min(100, Number(pct.toFixed(2))))
    return bounded
  }, [raw?.percentage, current, total])

  if (isLoading) return <Text>Loading export status...</Text>
  if (isError) return <Text c='red'>Failed to load export status.</Text>
  if (!raw) return null

  const isComplete = Boolean(raw.completedAt) || raw.status === 'completed'
  const hasFailures = (raw.failedCount ?? 0) > 0
  const errors = raw.errors ?? []

  const briefColumns = [
    { key: 'companyName', title: 'Company' },
    { key: 'firstName', title: 'First name' },
    { key: 'lastName', title: 'Last name' },
    { key: 'email', title: 'Email' },
  ]

  return (
    <Box>
      <Group justify='space-between' mt='xs'>
        <Text size='sm'>Processed: {processed}</Text>
        <Text size='sm'>Total: {total}</Text>
      </Group>

      <Progress value={progressValue} mt='xs' />

      <Group mt='xs' gap='sm' align='center'>
        <Text size='sm'>
          Status:{' '}
          <Badge
            variant='light'
            color={raw.status === 'completed' ? 'teal' : raw.status === 'failed' ? 'red' : 'blue'}
          >
            {raw.status}
          </Badge>
        </Text>
        <Badge variant='light'>Success: {raw.successCount ?? 0}</Badge>
        <Badge variant='light' color={hasFailures ? 'yellow' : 'gray'}>
          Failed: {raw.failedCount ?? 0}
        </Badge>
        {raw.ldrName && (
          <Badge variant='light' color='grape'>
            LDR: {raw.ldrName}
          </Badge>
        )}
      </Group>

      {isComplete && !hasFailures && (
        <Text size='sm' mt='xs' c='teal'>
          Export completed successfully!
        </Text>
      )}

      {raw.status === 'failed' && (
        <Text size='sm' mt='xs' c='red'>
          Export failed. Please try again.
        </Text>
      )}

      {hasFailures && (
        <>
          <Divider my='md' />
          <Text fw={600} size='sm' mb='xs'>
            Failed rows ({errors.length})
          </Text>

          {/* Summary table */}
          <ScrollArea h={200} type='auto'>
            <Table striped highlightOnHover withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>#</Table.Th>
                  {briefColumns.map((c) => (
                    <Table.Th key={c.key}>{c.title}</Table.Th>
                  ))}
                  <Table.Th>Error</Table.Th>
                  <Table.Th>Timestamp</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {failedRows.map((e: ExportError, idx: number) => (
                  <Table.Tr key={`${e.email}-${e.timestamp}`}>
                    <Table.Td>{idx + 1}</Table.Td>

                    {briefColumns.map((c) => {
                      const value = e[c.key as keyof ExportError]

                      return (
                        <Table.Td key={c.key}>
                          {typeof value === 'string' || typeof value === 'number' ? (
                            value
                          ) : (
                            <Text c='dimmed'>—</Text>
                          )}
                        </Table.Td>
                      )
                    })}

                    <Table.Td>
                      <Text size='sm' c='red'>
                        {typeof e.error === 'string' ? e.error : 'Error'}
                      </Text>
                    </Table.Td>

                    <Table.Td>
                      <Text size='xs' c='dimmed'>
                        {e.timestamp ? new Date(e.timestamp).toLocaleString() : '—'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </>
      )}
    </Box>
  )
}

export default LeadExportProgress
